import { useState } from "react";
import { CheckCircle2, Circle, Loader2, BellRing } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { formatDateTime } from "@/lib/format";
import { initials } from "./utils";

export function MeetingSignatures({ m, meetingId }) {
  const [reminding, setReminding] = useState(false);

  const pendingSigners = (m.attendees || []).filter(
    (a) => a.user_id && a.present !== false && !m.signatures?.some((s) => s.user_id === a.user_id)
  );

  const remind = async () => {
    setReminding(true);
    try {
      const { data } = await api.post(`/meetings/${meetingId}/remind-signers`);
      if (data.count) toast.success(`Påminnelse sendt til ${data.count} person(er)`);
      else if (data.failed?.length) toast.error("Kunne ikke sende påminnelse akkurat nå. Prøv igjen senere.");
      else toast.info(data.message || "Alle har allerede signert");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setReminding(false);
    }
  };

  return (
    <>
      <div className="rounded-xl border border-border/60 bg-card p-6 shadow-sm">
        <h3 className="font-headings text-sm font-bold uppercase tracking-wider text-muted-foreground">Deltakere</h3>
        <div className="mt-4 space-y-3">
          {m.attendees?.map((a) => (
            <div key={a.user_id || a.name} className="flex items-center gap-3">
              <Avatar className="h-8 w-8"><AvatarFallback className="bg-primary/10 text-xs font-semibold text-primary">{initials(a.name)}</AvatarFallback></Avatar>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{a.name}</p>
                <p className="text-xs text-muted-foreground">{a.role}</p>
              </div>
              {a.present ? <span className="text-xs text-emerald-600 dark:text-emerald-400">Til stede</span> : <span className="text-xs text-muted-foreground">Fraværende</span>}
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-border/60 bg-card p-6 shadow-sm">
        <div className="flex items-center justify-between gap-2">
          <h3 className="font-headings text-sm font-bold uppercase tracking-wider text-muted-foreground">Signaturer</h3>
          {pendingSigners.length > 0 ? (
            <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs" data-testid="remind-signers-btn" onClick={remind} disabled={reminding}>
              {reminding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <BellRing className="h-3.5 w-3.5" />} Send påminnelse
            </Button>
          ) : null}
        </div>
        <div className="mt-4 space-y-3">
          {m.attendees?.map((a) => {
            const sig = m.signatures?.find((s) => s.user_id === a.user_id);
            return (
              <div key={a.user_id || a.name} className="flex items-center gap-3">
                {sig ? <CheckCircle2 className="h-5 w-5 text-emerald-500" /> : <Circle className="h-5 w-5 text-muted-foreground/40" />}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{a.name}</p>
                  <p className="text-xs text-muted-foreground">{sig ? `Signert ${formatDateTime(sig.signed_at)}` : "Ikke signert"}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
